import { BP_COMPONENTS_URL, BP_COMPONENTS_VERSION } from "~/constants";

import { env } from "./helpers";

import type { RouteType } from "~/constants";

export interface URLOptions {
  includeBasePath?: boolean;
}

/**
 * Constructs a url pathname given a list parts.
 */
export function joinPath(...parts: string[]) {
  return parts
    .filter(Boolean)
    .map((p) => p.replace(/^\/|\/$/g, ""))
    .join("/");
}

/**
 * Builds the base URL for internal fetch requests.
 */
/**
 * Builds the base URL for internal fetch requests.
 */
export function getBaseUrl(options?: URLOptions) {
  const { includeBasePath = true } = options || {};

  const host = env("HOST").replace(/\/$/, "");
  const isLocal = /localhost/.test(host);
  const protocol = host ? (isLocal ? "http://" : "https://") : "";
  const port = isLocal ? env("PORT").replace(/^(\d+)/, ":$1") : "";
  const basePath = env("BASE_PATH", import.meta.env?.BASE_URL);

  return [protocol, host, port, includeBasePath ? basePath : ""]
    .join("")
    .replace(/\/$/, "");
}

/**
 * Builds a fully-qualified internal URL.
 */
export function getFullUrl(pathname: string, options?: URLOptions) {
  return /^http[s]?:\/\//.test(pathname)
    ? pathname
    : `${getBaseUrl(options)}/${pathname.replace(/^\//, "")}`;
}

/**
 * Builds an asset URL.
 */
export function getAssetUrl(assetPath: string) {
  return getFullUrl(assetPath);
}

/**
 * Builds the URL for a bp-web-components asset.
 */
export function getBpComponentsUrl(pathname = "") {
  return joinPath(BP_COMPONENTS_URL, BP_COMPONENTS_VERSION, pathname);
}

/**
 * Parses a route segment and returns parts
 */
export function parseRouteSegment(segment?: string) {
  const result = segment?.match(
    /^\/?(?<type>(\w+))-(?<position>\d+)(-(?<slug>.+))?/,
  );
  const { type, position, slug } = result?.groups ?? {};
  return {
    type: type ?? null,
    position: position ?? null,
    slug: slug ?? null,
  };
}

/**
 * Generates a relative URL path for a route.
 */
export function pathFor(
  type: RouteType,
  params?: Record<string, string | undefined>,
) {
  return type.replace(/:(\w+)/g, (_, key: string) => {
    return String(params?.[key]);
  });
}

/**
 * Generates a fully-qualified URL for a route.
 */
export function urlFor(
  type: RouteType,
  params?: Record<string, string | undefined>,
) {
  return getFullUrl(pathFor(type, params));
}
